import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  CalendarDays,
  ChevronLeft,
  CreditCard,
  LayoutGrid,
  LogOut,
  Menu,
  Plane,
  Receipt,
  Rocket,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { TailLockup } from './TailLockup';
import { AirbornePill } from './AirbornePill';
import { ThemeToggle } from './ThemeToggle';

interface NavItem {
  to: string;
  label: string;
}

const LESSEE_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard' },
  { to: '/book', label: 'Book a Flight' },
  { to: '/bookings', label: 'My Bookings' },
  { to: '/preflight', label: 'Pre-flight' },
  { to: '/flights', label: 'My Flights' },
  { to: '/logbook', label: 'Logbook' },
  { to: '/squawks', label: 'Squawks' },
  { to: '/tracker', label: 'Tracker' },
  { to: '/documents', label: 'Documents' },
  { to: '/billing', label: 'Billing' },
  { to: '/responsibilities', label: 'Responsibilities' },
  { to: '/resources', label: 'Aircraft & Avionics' },
  { to: '/profile', label: 'Profile' },
];

const ADMIN_NAV: NavItem[] = [
  { to: '/admin', label: 'Dashboard' },
  { to: '/admin/lessees', label: 'Lessees' },
  { to: '/admin/bookings', label: 'Schedule' },
  { to: '/admin/flights', label: 'Flights' },
  { to: '/admin/utilization', label: 'Utilization' },
  { to: '/admin/log-flight', label: 'Log flight' },
  { to: '/admin/logbook', label: 'Logbook' },
  { to: '/admin/tracker', label: 'Tracker' },
  { to: '/admin/checkout', label: 'Checkout sign-off' },
  { to: '/admin/squawks', label: 'Squawks' },
  { to: '/admin/aircraft', label: 'Aircraft' },
  { to: '/admin/resources', label: 'Aircraft & Avionics' },
  { to: '/admin/agreements', label: 'Agreements' },
  { to: '/admin/revenue', label: 'Revenue' },
  { to: '/admin/settings', label: 'Settings' },
  // Casey's Solari master console — a full navigation to the other app (same Google sign-in).
  { to: 'https://app.flysolari.com/platform', label: 'Solari Platform ↗' },
];

// A "Back to the previous screen" affordance users asked for (in addition to
// the nav menu). Shown only when there's in-app history to return to — react
// -router keeps a history index, so a deep-linked first page can never bounce
// the user out of the app.
function BackButton() {
  const nav = useNavigate();
  useLocation(); // re-render on every navigation so the history index is fresh
  const idx =
    window.history.state && typeof window.history.state.idx === 'number'
      ? window.history.state.idx
      : 0;
  if (idx <= 0) return null;
  return (
    <button
      type="button"
      onClick={() => nav(-1)}
      className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-ink-muted transition hover:text-navy-900"
    >
      <ChevronLeft size={18} /> Back
    </button>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const { profile, isAdmin, signOut, demoMode, isViewingAs, viewAsName, viewAs } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Always close the mobile menu when the route changes so it never "sticks open".
  useEffect(() => setMobileOpen(false), [loc.pathname]);

  // Carrier-named instructors get one extra entry: the checkout sign-off that feeds
  // the owner's Named-Insured renter approval.
  const items = isAdmin
    ? ADMIN_NAV
    : profile?.billingProfile === 'cfi'
    ? [...LESSEE_NAV, { to: '/checkout', label: 'Checkout Sign-off' }, { to: '/approve-flights', label: 'Approve Student Flights' }]
    : LESSEE_NAV;
  const displayName = profile?.personal?.firstName
    ? `${profile.personal.firstName} ${profile.personal.lastName ?? ''}`.trim()
    : profile?.email ?? '';
  const initials =
    (displayName || '?')
      .split(' ')
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';
  const dash = isAdmin ? '/admin' : '/';

  // Phone bottom bar. The sidebar has fifteen entries and a renter standing at
  // the airplane wants four of them; the rest stay behind More. Same shape as
  // the one in Solari, so the two apps feel like one product.
  const bottomBarItems = isAdmin
    ? [
        { to: '/admin', label: 'Home', Icon: LayoutGrid },
        { to: '/admin/bookings', label: 'Schedule', Icon: CalendarDays },
        { to: '/admin/flights', label: 'Flights', Icon: Plane },
        { to: '/admin/revenue', label: 'Revenue', Icon: Receipt },
      ]
    : [
        { to: '/', label: 'Home', Icon: LayoutGrid },
        { to: '/book', label: 'Book', Icon: Rocket },
        { to: '/bookings', label: 'Bookings', Icon: CalendarDays },
        { to: '/billing', label: 'Billing', Icon: CreditCard },
      ];

  async function handleSignOut() {
    await signOut();
    nav('/login', { replace: true });
  }

  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    `block rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
      isActive ? 'bg-brand text-white' : 'text-ink-muted hover:bg-slate-100 hover:text-navy-900'
    }`;

  return (
    <div className="min-h-screen bg-canvas">
      {isViewingAs && (
        <div className="sticky top-0 z-50 flex items-center justify-between gap-3 bg-amber-500 px-4 py-2 text-sm font-medium text-navy-900">
          <span className="truncate">
            👁 Viewing as <strong>{viewAsName}</strong> — read-only
          </span>
          <button
            onClick={() => {
              viewAs(null);
              nav('/admin/lessees');
            }}
            className="shrink-0 rounded-md bg-brand px-3 py-1 text-xs font-semibold text-white"
          >
            Exit to admin
          </button>
        </div>
      )}
      {/* ── Desktop sidebar (lg+) ── */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="px-5 py-5">
          <NavLink to={dash} aria-label="Go to dashboard" className="inline-block shrink-0">
            <TailLockup />
          </NavLink>
          {isAdmin && (
            <div className="mt-2">
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
                Admin
              </span>
            </div>
          )}
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-4">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.to === '/' || it.to === '/admin'} className={navItemClass}>
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-100 p-3">
          <div className="flex items-center gap-2.5 px-1">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-semibold text-white">
              {initials}
            </span>
            <div className="min-w-0">
              <div className="text-[11px] text-ink-subtle">Signed in</div>
              <div className="truncate text-sm font-medium text-navy-900">{displayName}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm text-ink-muted hover:bg-slate-100 hover:text-navy-900"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* ── Content column (offset on desktop for the fixed sidebar) ── */}
      <div className="flex min-h-screen flex-col lg:pl-64">
        {demoMode && (
          <div className="bg-amber-400 px-4 py-1.5 text-center text-xs font-medium text-amber-950 sm:text-sm">
            Preview mode — no Firebase backend. Data is mocked. Sign out to return to the picker.
          </div>
        )}
        {!isAdmin && (profile?.paymentPastDue || profile?.paymentFailedFlag) && (
          <div
            className={`px-4 py-2 text-center text-xs font-medium sm:text-sm ${
              profile?.paymentPastDue ? 'bg-red-600 text-white' : 'bg-amber-100 text-amber-900'
            }`}
          >
            {profile?.paymentPastDue
              ? 'Account past due — flying is paused until your balance is resolved. '
              : 'A recent payment didn’t go through. '}
            <NavLink to="/billing" className="font-semibold underline">
              Update your card →
            </NavLink>
          </div>
        )}

        {/* Mobile top bar (below lg) */}
        <header className="relative sticky top-0 z-30 border-b border-slate-200/70 bg-white/80 pt-[env(safe-area-inset-top)] backdrop-blur-xl lg:hidden">
          <div className="flex h-16 items-center justify-between pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] sm:px-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="-ml-2 p-2 text-navy-900"
                onClick={() => setMobileOpen((v) => !v)}
                aria-label="Toggle navigation"
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
              <NavLink to={dash} aria-label="Go to dashboard">
                <TailLockup compact />
              </NavLink>
              {isAdmin && (
                <span className="hidden sm:inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
                  Admin
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-ink-muted hover:bg-slate-100 hover:text-navy-900"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>

          {mobileOpen && (
            <>
              <div
                className="fixed inset-0 z-20 bg-navy-900/20"
                onClick={() => setMobileOpen(false)}
                aria-hidden
              />
              <nav className="absolute left-0 right-0 top-full z-30 flex max-h-[72vh] flex-col gap-1 overflow-y-auto border-b border-slate-200 bg-white px-4 pb-3 pt-1 shadow-xl">
                {items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.to === '/' || it.to === '/admin'}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `rounded-lg px-3 py-2.5 text-sm font-medium ${
                        isActive ? 'bg-brand text-white' : 'text-ink-muted hover:bg-slate-100 hover:text-navy-900'
                      }`
                    }
                  >
                    {it.label}
                  </NavLink>
                ))}
              </nav>
            </>
          )}
        </header>

        <main className="flex-1 w-full pb-20 lg:pb-0">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <BackButton />
            {/* One instance for the whole app: every page, every role. It renders
                nothing while the aircraft is parked, so it costs no space then.
                The wrapper is load-bearing: the pill and BackButton are both
                inline-level, so without a block between them they share a line and
                the pill sits on top of "Back". An empty div collapses to nothing,
                which keeps the parked state free. */}
            <div>
              <AirbornePill to={isAdmin ? '/admin/tracker' : '/tracker'} className="mb-5" />
            </div>
            {children}
          </div>
        </main>

        {/* Phone bottom bar — the places people actually go, plus the full menu. */}
        <nav
          className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-slate-200 bg-white/95 backdrop-blur-xl lg:hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          aria-label="Primary"
        >
          {bottomBarItems.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/' || to === '/admin'}
              className={({ isActive }) =>
                `flex min-h-[56px] flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${
                  isActive ? 'text-navy-900' : 'text-ink-muted'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={22} strokeWidth={isActive ? 2.4 : 1.8} />
                  {label}
                </>
              )}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => {
              window.scrollTo({ top: 0 });
              setMobileOpen(true);
            }}
            className="flex min-h-[56px] flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-ink-muted"
          >
            <Menu size={22} strokeWidth={1.8} />
            More
          </button>
        </nav>

        <footer className="border-t border-navy-900/10 bg-white py-4 text-center text-xs text-ink-muted">
          <div className="mb-3 flex justify-center">
            <ThemeToggle />
          </div>
          <div>
            AJM Aviation Ventures LLC · Lessee Portal · v2.1
            <span className="mx-2">·</span>
            <NavLink to="/privacy" className="hover:text-navy-900">Privacy</NavLink>
            <span className="mx-1.5">·</span>
            <NavLink to="/terms" className="hover:text-navy-900">Terms</NavLink>
          </div>
          <div className="mt-1 text-[11px] text-ink-subtle">
            Powered by <span className="font-medium text-navy-700/70">Solari</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
